import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import useDocumentTitle from '../hooks/useDocumentTitle'

export default function LoginPage() {
  useDocumentTitle('Login')

  const { user, loading, signIn, sendPasswordResetEmail } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mode, setMode] = useState('login')
  const [submitting, setSubmitting] = useState(false)
  const [resetEmailSentTo, setResetEmailSentTo] = useState('')

  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message, {
        id: 'login-status-message',
      })
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (mode === 'forgot-password') {
      if (!email.trim()) {
        toast.error('Please enter your email address')
        return
      }

      setSubmitting(true)

      try {
        await sendPasswordResetEmail(email.trim())
        setResetEmailSentTo(email.trim())
        toast.success('Password reset email sent')
        return
      } catch (err) {
        toast.error(err.message || 'Unable to send reset email')
      } finally {
        setSubmitting(false)
      }

      return
    }

    if (!email.trim() || !password.trim()) {
      toast.error('Please fill in all fields')
      return
    }

    setSubmitting(true)

    try {
      await signIn(email.trim(), password)
      toast.success('Welcome back!')
    } catch (err) {
      toast.error(err.message || 'Invalid credentials')
    } finally {
      setSubmitting(false)
    }
  }

  const switchToForgotPassword = () => {
    setMode('forgot-password')
    setPassword('')
  }

  const switchToLogin = () => {
    setMode('login')
    setResetEmailSentTo('')
  }

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-branding">
          <div className="login-logo">
            <div className="logo-icon large">K</div>
          </div>
          <h1>Kleezy Admin</h1>
          <p>Back-office management platform for Kleezy real estate services</p>
        </div>

        <div className="login-decoration">
          <div className="decoration-circle c1" />
          <div className="decoration-circle c2" />
          <div className="decoration-circle c3" />
        </div>
      </div>

      <div className="login-right">
        <div className="login-form-container">
          <div className="login-form-header">
            <h2>{mode === 'login' ? 'Sign in' : 'Forgot password'}</h2>
            <p>
              {mode === 'login'
                ? 'Enter your credentials to access the admin panel'
                : 'Enter your email and we will send you a password reset link'}
            </p>
          </div>

          <form className="login-form" onSubmit={handleSubmit}>
            {mode === 'forgot-password' && resetEmailSentTo ? (
              <div className="auth-message auth-message-success">
                Reset email has been sent to <strong>{resetEmailSentTo}</strong>.
                Open the link in that email to set a new password.
              </div>
            ) : null}

            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@kleezy.com"
                disabled={submitting}
                autoComplete="email"
                autoFocus
              />
            </div>

            {mode === 'login' ? (
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="********"
                  disabled={submitting}
                  autoComplete="current-password"
                />
              </div>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="loading-spinner small" />
                  {mode === 'login' ? 'Signing in...' : 'Sending reset email...'}
                </>
              ) : mode === 'login' ? (
                'Sign in'
              ) : (
                'Send reset email'
              )}
            </button>
          </form>

          <div className="auth-link-group">
            {mode === 'login' ? (
              <button
                type="button"
                className="btn btn-ghost auth-link-button"
                onClick={switchToForgotPassword}
                disabled={submitting}
              >
                Forgot your password?
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-ghost auth-link-button"
                onClick={switchToLogin}
                disabled={submitting}
              >
                Back to sign in
              </button>
            )}
          </div>

          <div className="login-footer">
            <p>
              Kleezy Admin Panel &middot; {new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
